import { Ban, ChevronDown, Footprints, Hash, PauseCircle } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlowCardGlyph } from './flow-card-token';
import { FlowPickerMenu, type PickerSection } from './flow-picker-menu';
import { ARMED_ROW, FlowRemoveButton } from './flow-remove-button';

import type { RuleProblemSeverity } from '@/lib/flow-utils';
import type { Block, Task } from '@/types/experience';
import type { AttemptCompare, AttemptFilter, AttemptWindowName, FlowCondition } from '@agavi/blocks';

import { AddRowButton } from '@/components/ui/add-row-button';
import { NumberField } from '@/components/ui/number-field';
import SelectMenu from '@/components/ui/select-menu';
import {
  conditionBlockIds, conditionKey, flowStateLabel, getBlockLabel, getFeedbackState,
  getInactiveSelectedStateIds, getStaleStateIds, gradableBlocks, inactiveReasonFor, namedStatesFor,
} from '@/lib/flow-utils';

/**
 * The list of things that have to be true, shared by a navigation event and a change.
 *
 * This replaces the block-by-state GRID, which is now deleted. The grid rendered every gradable
 * block against every one of its states as a toggle, so a card showed mostly what it did NOT match,
 * and a task with five blocks produced a wall a teacher had to read in full to find the two cells
 * that were on. A list is opt-in: it shows exactly when this fires and nothing else.
 *
 * A condition is authored the same way it reads: two dropdowns in a sentence. The grid survived one
 * round as the inside of the add picker, on the theory that showing everything is the right job for
 * a picker — but a picker that makes you scan every block to find one state is the same wall with a
 * button in front of it.
 *
 * Conditions are kinds, not one shape with optional fields, so that adding the next kind (a
 * competency rating, elapsed time) is a new variant rather than another nullable column — the rule
 * the block-issue work settled on. Everything below switches on `kind` exhaustively for that reason.
 */

/**
 * Written out rather than assembled as `t(`...attemptsFilter.${f}`)`.
 *
 * A key built at runtime is one typo away from rendering its own identifier on screen, and this
 * codebase has shipped that bug once already (a Flow chip that read "Is blocks.mcq.chipAnyOther").
 * An invariant caps how many of these may exist; four closed sets of four is not where to spend it.
 */
const FILTER_KEYS: Record<AttemptFilter, string> = {
  all: 'task.flow.conditions.attemptsFilter.all',
  complete: 'task.flow.conditions.attemptsFilter.complete',
  unique: 'task.flow.conditions.attemptsFilter.unique',
  incorrect: 'task.flow.conditions.attemptsFilter.incorrect',
};

const FILTERS = ['all', 'complete', 'unique', 'incorrect'] as const;

const COMPARE_KEYS: Record<AttemptCompare['op'], string> = {
  atLeast: 'task.flow.conditions.attemptsCompare.atLeast',
  moreThan: 'task.flow.conditions.attemptsCompare.moreThan',
  atMost: 'task.flow.conditions.attemptsCompare.atMost',
  fewerThan: 'task.flow.conditions.attemptsCompare.fewerThan',
  exactly: 'task.flow.conditions.attemptsCompare.exactly',
  between: 'task.flow.conditions.attemptsCompare.between',
};

/*
 * `= < ≤ > ≥ ↔`, the order every value-comparison picker in the app already uses — numerical,
 * slider and angle each declare it twice and all four agree. A teacher meets this row of symbols in
 * four places; it should not reshuffle in the fifth.
 */
const COMPARES = ['exactly', 'fewerThan', 'atMost', 'moreThan', 'atLeast', 'between'] as const;

const DEFAULT_ATTEMPTS: Extract<FlowCondition, { kind: 'attempts' }> = {
  kind: 'attempts',
  subject: { scope: 'task' },
  filter: 'all',
  compare: { op: 'atLeast', n: 3 },
};

/*
 * `taskId: null` is THIS task, which is what a teacher adding a visit condition almost always
 * means -- "the second time they come back here". `atLeast 2` for the same reason: at least one
 * visit is true the moment they arrive, so it is the only starting value that is never a rule
 * that fires immediately.
 */
const DEFAULT_VISITS: Extract<FlowCondition, { kind: 'visits' }> = {
  kind: 'visits',
  taskId: null,
  compare: { op: 'atLeast', n: 2 },
};

interface FlowConditionListProps {
  conditions: FlowCondition[];
  blocks: Block[];
  /** Every task in the experience, so a visit condition can name one other than this. */
  tasks: Task[];
  /** The task this flow belongs to — what a visit condition's `null` means. */
  currentTaskId: string;
  /**
   * Whether a student can arrive here twice — see `reenterableTasks`.
   *
   * Hides the attempts WINDOW menu when they cannot: on a task nobody returns to, this visit and
   * the whole session are the same attempts, and offering the choice invites a teacher to reason
   * about a difference that cannot occur. A stored `within: 'session'` still works; it simply
   * has nothing to disagree with.
   */
  reenterable: boolean;
  onChange: (conditions: FlowCondition[]) => void;
  /** Snapshot labels for a condition whose state has since been deleted — a navigation rule keeps
   * these, a change has nowhere to keep them, so it passes nothing and gets the generic name. */
  conditionLabels?: Record<string, Record<string, string>>;
  /** Joins the rows with the word that actually governs them — see the connector in the list. */
  matchMode: 'any' | 'all';
  /**
   * The rows at fault, by `conditionKey`, marked so an expanded card can point AT the problem
   * instead of tinting itself and leaving the teacher to find it.
   */
  problemKeys?: Set<string>;
  /** Which colour to mark them — see `RuleProblem`. */
  problemSeverity?: RuleProblemSeverity;
}

/** Whether two conditions are the same one — the identity an add/remove toggles on. */
function sameCondition(a: FlowCondition, b: FlowCondition): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'feedback' && b.kind === 'feedback') return a.blockId === b.blockId && a.stateId === b.stateId;
  return false;
}

export function FlowConditionList({ conditions, blocks, tasks, currentTaskId, reenterable, onChange, conditionLabels, matchMode, problemKeys, problemSeverity }: FlowConditionListProps) {
  const { t, i18n } = useTranslation();

  const holder = { conditions };
  // Only blocks with a state an event could target. A block whose every state is a default or
  // unreachable has nothing to offer, and offering it means a dropdown whose second half is empty.
  // Once All Correct is inert, each block's positive state becomes an ordinary condition —
  const eligible = gradableBlocks(blocks).filter((b) => namedStatesFor(b).length > 0);

  /** Adds what was chosen, so there is never a half-made condition in the model — and never a
   *  guess standing in for one, which is what appending "the first block's first state" was. */
  /** Every feedback condition already on this event, as picker keys. */
  const usedFeedbackKeys = new Set(
    conditions.flatMap((c) => (c.kind === 'feedback' ? [`${c.blockId}::${c.stateId}`] : [])),
  );

  function addFeedback(key: string) {
    const [blockId, stateId] = key.split('::');
    if (!blockId || !stateId) return;
    // Backstop. The menu greys an option that is already here, so this should be unreachable —
    // but appending a duplicate breaks the list's React keys, and that is not a failure mode worth
    // leaving one guard away.
    if (usedFeedbackKeys.has(key)) return;
    onChange([...conditions, { kind: 'feedback', blockId, stateId }]);
  }

  // What the Add Feedback menu centres on — the list, not the short button at its left edge.
  const listRef = useRef<HTMLDivElement>(null);

  function remove(index: number) {
    onChange(conditions.filter((_, i) => i !== index));
  }

  function toggleFeedback(blockId: string, stateId: string) {
    const condition: FlowCondition = { kind: 'feedback', blockId, stateId };
    const already = conditions.some((c) => sameCondition(c, condition));
    onChange(already ? conditions.filter((c) => !sameCondition(c, condition)) : [...conditions, condition]);
  }

  function patchAttempts(index: number, patch: Partial<Extract<FlowCondition, { kind: 'attempts' }>>) {
    onChange(conditions.map((c, i) => (i === index && c.kind === 'attempts' ? { ...c, ...patch } : c)));
  }

  function patchFeedback(index: number, patch: Partial<Extract<FlowCondition, { kind: 'feedback' }>>) {
    onChange(conditions.map((c, i) => (i === index && c.kind === 'feedback' ? { ...c, ...patch } : c)));
  }

  function patchVisits(index: number, patch: Partial<Extract<FlowCondition, { kind: 'visits' }>>) {
    onChange(conditions.map((c, i) => (i === index && c.kind === 'visits' ? { ...c, ...patch } : c)));
  }

  return (
    <div ref={listRef} className='flex flex-col gap-2'>
      {conditions.map((condition, index) => (
        <div key={conditionKey(condition, index)} className='flex flex-col gap-1'>
          {/* The word BETWEEN the cards, because that is where the relationship is.
              Two stacked cards say nothing about whether both have to hold or either will do, and
              the Any/All control sits above the whole list where it reads as a heading rather than
              as the joint between these two particular rows. */}
          {index > 0 && (
            <span className='px-2 text-[10px] font-semibold uppercase tracking-wide text-ws-muted'>
              {t(matchMode === 'any' ? 'task.flow.card.connectorOr' : 'task.flow.card.connectorAnd')}
            </span>
          )}
          <ConditionRow
            condition={condition}
            blocks={blocks}
            problem={problemKeys?.has(conditionKey(condition, index)) ? problemSeverity : undefined}
            onRemove={() => remove(index)}
            tasks={tasks}
            currentTaskId={currentTaskId}
            reenterable={reenterable}
            onPatchAttempts={(patch) => patchAttempts(index, patch)}
            onPatchFeedback={(patch) => patchFeedback(index, patch)}
            onPatchVisits={(patch) => patchVisits(index, patch)}
          />
        </div>
      ))}

      {/* Every stale/unreachable reference a teacher still has to deal with. These are NOT rendered
          from `conditions` above, because the state they name no longer resolves to anything the
          normal chip can label — they carry their own marker and their own snapshot name. */}
      {conditionBlockIds(holder).map((blockId) => {
        const block = blocks.find((b) => b.id === blockId);
        const stale = getStaleStateIds(holder, blockId, blocks);
        const inactive = block ? getInactiveSelectedStateIds(holder, blockId, blocks) : [];
        if (stale.length === 0 && inactive.length === 0) return null;
        const blockLabel = block
          ? getBlockLabel(block, blocks.findIndex((b) => b.id === blockId), t)
          : t('task.flow.rule.unknownBlock');
        return (
          <div key={`broken-${blockId}`} className='flex flex-col gap-1'>
            {inactive.map((id) => {
              const state = block && getFeedbackState(block, id);
              const reasonKey = block && state ? inactiveReasonFor(block, state) : undefined;
              const reason = reasonKey ? t(`task.flow.rule.unreachableReason.${reasonKey}`) : '';
              return (
                <BrokenRow
                  key={id}
                  icon={<PauseCircle aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />}
                  blockLabel={blockLabel}
                  stateLabel={block && state ? flowStateLabel(state, t, block, i18n.language) : t('task.flow.rule.unknownFeedback')}
                  removeLabel={t('task.flow.rule.removeInactiveCondition', { reason })}
                  onRemove={() => toggleFeedback(blockId, id)}
                />
              );
            })}
            {stale.map((id) => {
              const label = conditionLabels?.[blockId]?.[id] ?? t('task.flow.rule.unknownFeedback');
              return (
                <BrokenRow
                  key={id}
                  icon={<Ban aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />}
                  blockLabel={blockLabel}
                  stateLabel={label}
                  removeLabel={t('task.flow.rule.deletedConditionTooltip', { feedback: label })}
                  onRemove={() => toggleFeedback(blockId, id)}
                />
              );
            })}
          </div>
        );
      })}

      {/* One button per kind, no submenu: there are few enough kinds to show them all, and a
          submenu would hide the one fact worth seeing here — what a condition can be ABOUT.

          IN THE ORDER THE SYSTEM LEARNS EACH FACT: it knows the visit on arrival, the attempt on
          submit, and the feedback only once that attempt is graded. A teacher reading the row
          left to right is reading the submission's own timeline.

          The plus carries the act and the word carries the kind, which is `AddRowButton`'s own
          default. Kind glyphs were tried here and cost the row its single line — three icons plus
          three "Add …" labels wrap in a 370px panel, and wrapped buttons read as two groups. */}
      <div className='flex flex-wrap items-center gap-1.5'>
        <AddRowButton
          inline
          label={t('task.flow.conditions.kindVisits')}
          ariaLabel={t('task.flow.conditions.addVisits')}
          onClick={() => onChange([...conditions, DEFAULT_VISITS])}
        />
        <AddRowButton
          inline
          label={t('task.flow.conditions.kindAttempts')}
          ariaLabel={t('task.flow.conditions.addAttempts')}
          onClick={() => onChange([...conditions, DEFAULT_ATTEMPTS])}
        />
        <FlowPickerMenu
          alignTo={listRef}
          sections={feedbackSections(blocks, null, t, i18n.language, usedFeedbackKeys)}
          onChoose={addFeedback}
          ariaLabel={t('task.flow.conditions.feedbackStateLabel')}
          filterPlaceholder={t('task.flow.conditions.filterFeedback')}
          noMatchesLabel={t('task.flow.card.noMatches')}
          renderTrigger={(p) => (
            <AddRowButton
              {...p}
              inline
              label={t('task.flow.conditions.kindFeedback')}
              ariaLabel={t('task.flow.conditions.addFeedback')}
              disabled={eligible.length === 0}
            />
          )}
        />
      </div>
    </div>
  );
}

/** A condition whose state is gone or switched off: named from a snapshot, removable, never
 * silently dropped — a rule that quietly stops naming what a teacher chose is worse than one that
 * says the choice has broken. */
function BrokenRow({ icon, blockLabel, stateLabel, removeLabel, onRemove }: {
  icon: React.ReactNode; blockLabel: string; stateLabel: string; removeLabel: string; onRemove: () => void;
}) {
  const { t } = useTranslation();
  const [armed, setArmed] = useState(false);
  return (
    <div className={`flex items-start gap-1.5 rounded-lg border border-dashed px-2 py-1.5 text-xs italic transition-colors ${
      armed ? ARMED_ROW : 'border-ws-border text-ws-muted'
    }`}>
      {icon}
      {/* Two lines, for the same reason as the editable row above. */}
      <span className='min-w-0 flex-1 line-clamp-2 break-words'>{blockLabel} · {stateLabel}</span>
      <FlowRemoveButton label={removeLabel} confirmLabel={t('common.confirmDelete')}
        armed={armed} onArmedChange={setArmed} onClick={onRemove} />
    </div>
  );
}

/** The outline a row wears when it is the reason its event is paused or broken. */
const PROBLEM_BORDER: Record<RuleProblemSeverity, string> = {
  error: 'border-ws-error-fg bg-ws-error-bg/20',
  warning: 'border-ws-warning-fg bg-ws-warning-fg/[.06]',
};

function ConditionRow({ condition, blocks, tasks, currentTaskId, reenterable, problem, onRemove, onPatchAttempts, onPatchFeedback, onPatchVisits }: {
  condition: FlowCondition;
  blocks: Block[];
  tasks: Task[];
  currentTaskId: string;
  reenterable: boolean;
  problem?: RuleProblemSeverity;
  onRemove: () => void;
  onPatchAttempts: (patch: Partial<Extract<FlowCondition, { kind: 'attempts' }>>) => void;
  onPatchFeedback: (patch: Partial<Extract<FlowCondition, { kind: 'feedback' }>>) => void;
  onPatchVisits: (patch: Partial<Extract<FlowCondition, { kind: 'visits' }>>) => void;
}) {
  const { t, i18n } = useTranslation();
  const [armed, setArmed] = useState(false);
  const rowClass = armed ? ARMED_ROW : problem ? PROBLEM_BORDER[problem] : 'border-ws-border';

  if (condition.kind === 'feedback') {
    const block = blocks.find((b) => b.id === condition.blockId);
    const state = block && getFeedbackState(block, condition.stateId);
    // A broken reference renders in its own section (see BrokenRow) with its own marker — this row
    // is for a condition that still resolves.
    if (!block || !state) return null;
    const blockLabel = getBlockLabel(block, blocks.findIndex((b) => b.id === condition.blockId), t);

    return (
      /* ONE row: the sentence, then the bin. The caption line above it said "FEEDBACK" beside a
         glyph the trigger repeats two lines later, and cost a third of the card's height to say
         what the row's own shape already says. */
      <div className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 transition-colors ${rowClass}`}>
        {/* The whole sentence is the trigger: re-pointing a condition is one act, so it is one
            control. Two dropdowns made it two, and made the first one edit the second. */}
        <FlowPickerMenu
          className='flex-1'
          sections={feedbackSections(blocks, `${condition.blockId}::${condition.stateId}`, t, i18n.language)}
          onChoose={(key) => {
            const [blockId, stateId] = key.split('::');
            if (blockId && stateId) onPatchFeedback({ blockId, stateId });
          }}
          ariaLabel={t('task.flow.conditions.feedbackStateLabel')}
          filterPlaceholder={t('task.flow.conditions.filterFeedback')}
          noMatchesLabel={t('task.flow.card.noMatches')}
          renderTrigger={(p) => (
            <button
              {...p}
              type='button'
              aria-label={t('task.flow.conditions.feedbackStateLabel')}
              className='flex min-w-0 flex-1 items-center gap-1 rounded px-1 py-0.5 -mx-1 text-left text-xs text-ws-text hover:bg-ws-hover focus-visible:[outline:2px_auto]'
            >
              {/*
                * ONE TEXT FLOW, not four flex items.
                *
                * As flex items the state had to be given a basis, and at this panel's width any
                * basis wide enough to hold "(Any Version, Any Branch) Default Incorrect" forced it
                * onto its own line every time — stranding "MCQ 1 is ◆" on a short first line with
                * the rest of that line empty. Laid out as text it simply flows: the state starts
                * beside the glyph and continues onto the second line, with no gap anywhere.
                *
                * The block NAME keeps a cap, because the bug the old 2:3 split existed to prevent
                * is real — unbounded, a long name ate the whole row and the card said "Bromeliad
                * Trail Start GPS is …" without ever naming the feedback, which is the one thing a
                * condition is for. A name truncates legibly and carries its full text in `title`;
                * the scope tag does not, so the name is the half that gives way.
                *
                * Two lines on the whole flow, for the reason the surfaces above share: these rows
                * stack, and a rule with four conditions is already tall.
                */}
              <span className='min-w-0 flex-1 line-clamp-2 break-words'>
                <span className='inline-block max-w-[45%] truncate align-bottom font-medium' title={blockLabel}>{blockLabel}</span>
                {' '}
                <span className='text-ws-muted'>{t('task.flow.conditions.feedbackIs')}</span>
                {' '}
                <FlowCardGlyph evaluation={state.evaluation} className='inline h-3.5 w-3.5 align-text-bottom' />
                {' '}
                {flowStateLabel(state, t, block, i18n.language)}
              </span>
              <ChevronDown className='h-3.5 w-3.5 shrink-0 opacity-60' aria-hidden='true' />
            </button>
          )}
        />
        <FlowRemoveButton
          label={t('task.flow.conditions.removeFeedback', { block: blockLabel })}
          confirmLabel={t('common.confirmDelete')}
          armed={armed}
          onArmedChange={setArmed}
          onClick={onRemove}
        />
      </div>
    );
  }

  // ── visits ────────────────────────────────────────────────────────────────
  if (condition.kind === 'visits') {
    /*
     * "Visits to <task> <≥> <2>". ONE line, unlike attempts: there is no filter and no block, so
     * the sentence has one clause rather than two and breaking it would be padding.
     *
     * The task menu leads with this one, because naming somewhere else is the rarer reading and
     * the common one should not be found by scrolling. A target that has since been DELETED keeps
     * its own option at the bottom, labelled from the snapshot: dropping it would silently
     * re-point the condition at this task, which is a different rule.
     */
    const { taskId } = condition;
    const live = taskId !== null && tasks.some((tk) => tk.id === taskId);
    return (
      <div className={`flex items-start rounded-lg border px-2 py-1.5 transition-colors ${rowClass}`}>
        <div className='flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-ws-text'>
          <span>{t('task.flow.conditions.visitsTo')}</span>
          <SelectMenu
            inline
            ariaLabel={t('task.flow.conditions.visitsTaskLabel')}
            value={taskId ?? HERE}
            onChange={(v) => onPatchVisits({
              taskId: v === HERE ? null : v,
              // Snapshotted on the way in, so the row can still name a task that is later deleted.
              taskTitle: v === HERE ? undefined : tasks.find((tk) => tk.id === v)?.title || undefined,
            })}
            options={[
              { value: HERE, label: t('task.flow.conditions.visitsTaskHere') },
              ...tasks
                .filter((tk) => tk.id !== currentTaskId)
                .map((tk) => ({ value: tk.id, label: tk.title || t('task.flow.conditions.visitsTaskUntitled') })),
              ...(taskId !== null && !live
                ? [{ value: taskId, label: t('task.flow.conditions.visitsTaskDeleted', {
                    task: condition.taskTitle || t('task.flow.conditions.visitsTaskUntitled'),
                  }) }]
                : []),
            ]}
          />
          <CompareControls
            compare={condition.compare}
            onChange={(next) => onPatchVisits({ compare: next })}
            countLabel={t('task.flow.conditions.visitsCountLabel')}
          />
          <span className='ml-auto'>
            <FlowRemoveButton
              label={t('task.flow.conditions.removeVisits')}
              confirmLabel={t('common.confirmDelete')}
              armed={armed}
              onArmedChange={setArmed}
              onClick={onRemove}
            />
          </span>
        </div>
      </div>
    );
  }

  // ── attempts ──────────────────────────────────────────────────────────────
  const { subject, filter, compare } = condition;
  const subjectValue = subject.scope === 'task' ? 'task' : subject.blockId;

  return (
    <div className={`flex items-start rounded-lg border px-2 py-1.5 transition-colors ${rowClass}`}>
      {/* A sentence, not a form: which attempts, on what, compared how. Each menu is inline so it
          reads as words rather than as unlabelled boxes.
          The caption line this used to sit under is gone, for the reason the feedback row's went:
          it named the kind that the leading icon and the sentence already name.

          TWO LINES, and the break is where the sentence's own clause ends: WHICH attempts, on
          WHAT, is the subject; how many, the test applied to it. Left to wrap on its own the
          break fell wherever the block name happened to run out of room, so the same condition
          read differently from one block to the next and a long name could orphan "compared how"
          onto a line of its own. */}
      <div className='flex min-w-0 flex-1 flex-col gap-y-1 text-xs text-ws-text'>
        <div className='flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1'>
        <SelectMenu
          inline
          ariaLabel={t('task.flow.conditions.attemptsFilterLabel')}
          value={filter}
          onChange={(v) => onPatchAttempts({ filter: v as AttemptFilter })}
          options={FILTERS.map((f) => ({ value: f, label: t(FILTER_KEYS[f]) }))}
        />
        <span>{t('task.flow.conditions.attemptsOn')}</span>
        <SelectMenu
          inline
          ariaLabel={t('task.flow.conditions.attemptsSubjectLabel')}
          value={subjectValue}
          onChange={(v) => onPatchAttempts({ subject: v === 'task' ? { scope: 'task' } : { scope: 'block', blockId: v } })}
          options={[
            { value: 'task', label: t('task.flow.conditions.attemptsSubjectTask') },
            ...gradableBlocks(blocks).map((b) => ({
              value: b.id,
              label: getBlockLabel(b, blocks.findIndex((bl) => bl.id === b.id), t),
            })),
          ]}
        />
        {/* Only where a return is possible. Elsewhere the two windows are the same attempts. */}
        {reenterable && (
          <SelectMenu
            inline
            ariaLabel={t('task.flow.conditions.attemptsWindowLabel')}
            value={condition.within ?? 'visit'}
            onChange={(v) => onPatchAttempts({ within: v as AttemptWindowName })}
            options={[
              { value: 'visit', label: t('task.flow.conditions.attemptsWindowVisit') },
              { value: 'session', label: t('task.flow.conditions.attemptsWindowSession') },
            ]}
          />
        )}
        </div>
        <div className='flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1'>
        <CompareControls
          compare={compare}
          onChange={(next) => onPatchAttempts({ compare: next })}
          countLabel={t('task.flow.conditions.attemptsCountLabel')}
        />
        <span className='ml-auto'>
          <FlowRemoveButton
            label={t('task.flow.conditions.removeAttempts')}
            confirmLabel={t('common.confirmDelete')}
            armed={armed}
            onArmedChange={setArmed}
            onClick={onRemove}
          />
        </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The sentinel the task menu uses for "this task", because `null` is not a `<select>` value.
 *
 * A reserved string rather than the empty one: a task id is what every other option carries, and
 * an empty value is indistinguishable from a menu that has not been set.
 */
const HERE = '__here';

/**
 * The comparison half of a count condition: how, and how many.
 *
 * Shared by attempts and visits rather than written twice. The two ask about different numbers
 * but the same way, and a teacher meeting `≥ 3` on one row and a differently-built `≥ 3` on the
 * next is one drift away from them disagreeing about what a blank field means.
 */
function CompareControls({ compare, onChange, countLabel }: {
  compare: AttemptCompare;
  onChange: (next: AttemptCompare) => void;
  countLabel: string;
}) {
  const { t } = useTranslation();
  const isRange = compare.op === 'between';
  return (
    <>
      <SelectMenu
        inline
        ariaLabel={t('task.flow.conditions.attemptsCompareLabel')}
        value={compare.op}
        onChange={(v) => onChange(nextCompare(compare, v as AttemptCompare['op']))}
        options={COMPARES.map((op) => ({ value: op, label: t(COMPARE_KEYS[op]) }))}
      />
      <NumberField
        grammar='integer'
        value={String(isRange ? compare.lo : compare.n)}
        onChange={(v) => onChange(withLow(compare, v))}
        ariaLabel={isRange ? t('task.flow.conditions.attemptsFromLabel') : countLabel}
        className='w-14'
      />
      {isRange && (
        <>
          <span>{t('task.flow.conditions.attemptsAnd')}</span>
          <NumberField
            grammar='integer'
            value={String(compare.hi)}
            onChange={(v) => onChange({ op: 'between', lo: compare.lo, hi: toCount(v) })}
            ariaLabel={t('task.flow.conditions.attemptsToLabel')}
            className='w-14'
          />
        </>
      )}
    </>
  );
}

/** Blank while typing is a real intermediate state, not a zero — but the stored model has no room
 * for "no number yet", so an empty field reads as 1, the smallest count that means anything. */
function toCount(raw: string): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/** Switching comparison keeps the number the teacher already chose rather than resetting it —
 * "at least 5" to "at most 5" is one decision changed, not two. */
function nextCompare(current: AttemptCompare, op: AttemptCompare['op']): AttemptCompare {
  const n = current.op === 'between' ? current.lo : current.n;
  if (op === 'between') return { op, lo: n, hi: n + 2 };
  return { op, n };
}

function withLow(current: AttemptCompare, raw: string): AttemptCompare {
  const n = toCount(raw);
  return current.op === 'between' ? { op: 'between', lo: n, hi: Math.max(n, current.hi) } : { op: current.op, n };
}

/**
 * Every feedback a condition could name, grouped by block.
 *
 * One menu rather than two coupled dropdowns. The dropdowns worked, but changing the block had to
 * silently rewrite the state to that block's first — a control that edits a field you did not touch
 * — and a native `<option>` cannot carry the evaluation glyph, so you only saw what kind of feedback
 * you had picked after picking it. Grouped and filterable is also the only version that stays
 * usable on a task with six blocks.
 */
function feedbackSections(
  blocks: Block[],
  selectedKey: string | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
  locale?: string,
  /**
   * Conditions the event already has, greyed so the same one cannot be added twice.
   *
   * It could, and it was not a cosmetic problem: two identical conditions are redundant under
   * either match mode, and the list keys its rows by `fb:<blockId>:<stateId>`, so a duplicate
   * collided on a React key. Greyed rather than hidden, for the reason the menu greys anything
   * else — an option that vanishes reads as a bug, one that is greyed with a reason answers the
   * question. The row it refers to is already on screen right behind the menu.
   */
  alreadyUsed?: Set<string>,
): PickerSection[] {
  // Derived from the same task's blocks the sections are built from, so the menu and the
  // eligibility filter above cannot disagree about whether the positive slot is on offer.
  return gradableBlocks(blocks)
    .map((b) => ({
      header: getBlockLabel(b, blocks.findIndex((bl) => bl.id === b.id), t),
      options: namedStatesFor(b).map((state) => {
        const key = `${b.id}::${state.id}`;
        return {
          key,
          text: flowStateLabel(state, t, b, locale),
          icon: <FlowCardGlyph evaluation={state.evaluation} />,
          selected: key === selectedKey,
          // Never greys the condition being RE-POINTED — that picker passes no set, because its
          // own current value is the one thing it must still be able to land on.
          disabled: alreadyUsed?.has(key) ? t('task.flow.conditions.alreadyAdded') : undefined,
        };
      }),
    }))
    .filter((sec) => sec.options.length > 0);
}

/** `atLeast` reads as "is at least" while you are setting it and as "≥" once you have; the symbols
 *  are language-neutral, so a collapsed row needs no strings of its own. */
const COMPARE_SYMBOL: Record<AttemptCompare['op'], string> = {
  atLeast: '\u2265', moreThan: '>', atMost: '\u2264', fewerThan: '<', exactly: '=', between: '',
};

/** The comparison as a collapsed row shows it. Shared, for the reason `CompareControls` is. */
function compareAmount(c: AttemptCompare): string {
  return c.op === 'between' ? `${c.lo}\u2013${c.hi}` : `${COMPARE_SYMBOL[c.op]} ${c.n}`;
}

/**
 * A collapsed event, read the way the built-in rungs read.
 *
 * "MCQ 1 is ⊠ Cachorro or Attempts on this task ≥ 5". The same feedback-card glyph the rungs use,
 * and the same joining word the card's own match mode is set to — a collapsed card that said "or"
 * while the open one said "all" would be describing a different event.
 */
export function FlowConditionSummary({ conditions, blocks, tasks, matchMode }: {
  conditions: FlowCondition[];
  blocks: Block[];
  /** Only to resolve a visit condition's target to its LIVE title; the snapshot is the fallback. */
  tasks: Task[];
  matchMode: 'any' | 'all';
}) {
  const { t, i18n } = useTranslation();
  if (conditions.length === 0) return null;
  const connector = t(matchMode === 'any' ? 'task.flow.card.connectorOr' : 'task.flow.card.connectorAnd');

  const label = (c: FlowCondition, key: string) => {
    if (c.kind === 'feedback') {
      const block = blocks.find((b) => b.id === c.blockId);
      const state = block && getFeedbackState(block, c.stateId);
      if (!block || !state) return null;
      return (
        <span key={key} className='inline-flex min-w-0 items-center gap-1 text-ws-text'>
          {/* `shrink-0`: the NAME is what tells a reader which block this is about, and it was
              being squeezed to nothing by a long state beside it. The state is the half that
              wraps. */}
          <span className='shrink-0 font-medium'>{getBlockLabel(block, blocks.findIndex((b) => b.id === c.blockId), t)}</span>
          <span className='shrink-0 text-ws-muted'>{t('task.flow.conditions.feedbackIs')}</span>
          <FlowCardGlyph evaluation={state.evaluation} className='h-3.5 w-3.5 shrink-0' />
          {/* Two lines: this is the special-event card's own summary, where the scope tag was
              being clipped mid-word. */}
          <span className='min-w-0 line-clamp-2 break-words'>{flowStateLabel(state, t, block, i18n.language)}</span>
        </span>
      );
    }
    if (c.kind === 'visits') {
      /* The live title wins over the snapshot, which is only there for a task that is gone. */
      const target = c.taskId === null
        ? t('task.flow.conditions.visitsTaskHere')
        : tasks.find((tk) => tk.id === c.taskId)?.title
          || c.taskTitle
          || t('task.flow.conditions.visitsTaskUntitled');
      return (
        <span key={key} className='inline-flex min-w-0 items-center gap-1 text-ws-text'>
          <Footprints aria-hidden='true' className='h-3.5 w-3.5 shrink-0 text-ws-muted' />
          <span className='min-w-0 truncate'>{t('task.flow.conditions.visitsTo')} {target}</span>
          <span className='font-medium tabular-nums'>{compareAmount(c.compare)}</span>
        </span>
      );
    }
    const subjectId = c.subject.scope === 'block' ? c.subject.blockId : null;
    const subjectBlock = subjectId ? blocks.find((b) => b.id === subjectId) : undefined;
    const subject = c.subject.scope === 'task'
      ? t('task.flow.conditions.attemptsSubjectTask')
      : subjectBlock ? getBlockLabel(subjectBlock, blocks.findIndex((b) => b.id === subjectBlock.id), t)
      : t('task.flow.rule.unknownBlock');
    return (
      <span key={key} className='inline-flex min-w-0 items-center gap-1 text-ws-text'>
        <Hash aria-hidden='true' className='h-3.5 w-3.5 shrink-0 text-ws-muted' />
        <span className='min-w-0 truncate'>
          {t(FILTER_KEYS[c.filter])} {t('task.flow.conditions.attemptsOn')} {subject}
          {/* Only the non-default window is named: a collapsed row saying "this visit" on every
              attempts condition would be noise on the ones that never cross a visit. */}
          {c.within === 'session' ? ` ${t('task.flow.conditions.attemptsWindowSession')}` : ''}
        </span>
        <span className='font-medium tabular-nums'>{compareAmount(c.compare)}</span>
      </span>
    );
  };

  const parts = conditions
    .map((c, i) => label(c, c.kind === 'feedback' ? `fb:${c.blockId}:${c.stateId}` : `${c.kind}:${i}`))
    .filter((x): x is React.ReactElement => x !== null);
  if (parts.length === 0) return null;

  return (
    <p className='flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-ws-muted'>
      {parts.map((part, i) => (
        <span key={part.key} className='inline-flex min-w-0 items-center gap-1.5'>
          {i > 0 && <span>{connector}</span>}
          {part}
        </span>
      ))}
    </p>
  );
}
